// Ogg container parser: pages, packets, and the codecs inside them
// (Vorbis, Opus, FLAC-in-Ogg and Speex).
//
// An Ogg file is a run of pages. Each page starts with "OggS", has a 27-byte
// header plus a lacing table, and carries pieces of packets for one logical
// stream (identified by a serial number). Packets can span pages. We walk the
// pages once, eagerly, to keep track of packets, timing and codec headers. The
// per-page detail (fields, packet children) is built lazily because a long
// file can have many thousands of pages.
import { ascii, BitFields, channelName, field, fmtBytes, fmtHz, fmtKbps, fmtSec, hex, i16le, matches, plural, u16be, u16le, u32le } from './bits';
import type { AudioInfo, Explanation, Field, FormatParser, Note, ParsedFile, Picture, Segment, SegmentKind, Tag } from './types';
import { parseFlacPicture, parseVorbisComment, vorbisCommentExplanations, type VorbisCommentResult } from './vorbiscomment';

// ---------------------------------------------------------------------------
// CRC-32 as used by Ogg: polynomial 0x04C11DB7, MSB first (not reflected),
// initial value 0, no final XOR. The CRC field itself counts as zero bytes.

// Slicing-by-8 tables: T[k][i] is the CRC of byte i followed by k zero bytes.
// Eight lookups per 8 bytes makes this about 4x faster than the simple loop.
const CRC_T = (() => {
  const t = Array.from({ length: 8 }, () => new Int32Array(256));
  for (let i = 0; i < 256; i++) {
    let r = i << 24;
    for (let j = 0; j < 8; j++) r = r & 0x80000000 ? (r << 1) ^ 0x04c11db7 : r << 1;
    t[0][i] = r;
  }
  for (let k = 1; k < 8; k++) for (let i = 0; i < 256; i++) t[k][i] = (t[k - 1][i] << 8) ^ t[0][(t[k - 1][i] >>> 24) & 0xff];
  return t;
})();
const [T0, T1, T2, T3, T4, T5, T6, T7] = CRC_T;

function crcUpdate(crc: number, b: Uint8Array, s: number, e: number): number {
  let i = s;
  for (; i + 8 <= e; i += 8) {
    const c = crc ^ ((b[i] << 24) | (b[i + 1] << 16) | (b[i + 2] << 8) | b[i + 3]);
    crc = T7[c >>> 24] ^ T6[(c >>> 16) & 0xff] ^ T5[(c >>> 8) & 0xff] ^ T4[c & 0xff] ^
      T3[b[i + 4]] ^ T2[b[i + 5]] ^ T1[b[i + 6]] ^ T0[b[i + 7]];
  }
  for (; i < e; i++) crc = (crc << 8) ^ T0[((crc >>> 24) ^ b[i]) & 0xff];
  return crc;
}

/** CRC of the page at [start, end), computed with the CRC field (bytes 22–25) as zeros. */
export function oggPageCrc(b: Uint8Array, start: number, end: number): number {
  let c = crcUpdate(0, b, start, start + 22);
  for (let i = 0; i < 4; i++) c = (c << 8) ^ T0[(c >>> 24) & 0xff];
  return crcUpdate(c, b, start + 26, end) >>> 0;
}

// ---------------------------------------------------------------------------
// Data kept per logical stream and per page.

type Codec = 'Vorbis' | 'Opus' | 'FLAC' | 'Speex' | 'Theora' | 'Skeleton' | 'Unknown';
const AUDIO_CODECS = new Set<Codec>(['Vorbis', 'Opus', 'FLAC', 'Speex']);

/** Where one piece of a packet lies: file range, and its offset inside the whole packet. */
interface Piece { fileStart: number; fileEnd: number; logical: number }

/** A parsed header packet in packet coordinates (offset 0 = first byte of the packet). */
interface Detail {
  kind: SegmentKind;
  type: string;
  name: string;
  summary?: string;
  fields: Field[];
  children: Segment[];
  /** 'comment' marks packets that hold tags, so their page is 'metadata'. */
  role?: 'comment';
  /** A picture with raw bytes, in packet coordinates. */
  picture?: Picture;
}

interface HeaderPacket { detail: Detail | null; pieces: Piece[] }

class Stream {
  codec: Codec = 'Unknown';
  codecLong = '';
  /** Packets before this number are codec headers; the rest are audio. */
  headerCount = 1;
  nextPacket = 0;
  // The packet that is still open at the end of the last page.
  open = false;
  openNo = 0;
  openLen = 0;
  // First bytes of the open packet: buf[headOff, headEnd), up to 16 bytes.
  headBuf: Uint8Array = new Uint8Array(0);
  headOff = 0;
  headEnd = 0;
  openHeader = false;
  openPieces: Piece[] = [];
  headers = new Map<number, HeaderPacket>();
  // Codec parameters.
  rate = 0;
  channels = 0;
  preSkip = 0;
  inputRate = 0;
  blocksizes: [number, number] = [0, 0];
  modeFlags: number[] | null = null;
  modeBits = 0;
  nominal = 0;
  vendor = '';
  bitsPerSample = 0;
  totalSamples = 0;
  flacMinBlock = 0;
  flacMaxBlock = 0;
  speexSamplesPerPacket = 0;
  tags: Tag[] = [];
  pictures: Picture[] = [];
  notes: Note[] = [];
  // Page bookkeeping.
  pages: Page[] = [];
  lastSeq = -1;
  eos = false;
  // Timing.
  prevBlock = 0; // Vorbis: size of the previous audio block (0 = none yet)
  base: number | null = null; // granule where the stream's audio starts
  lastAudioGranule = NaN;
  lastExpectedEnd = NaN; // granule the last page would have without end trimming
  durKnown = true;
  audioBytes = 0;
  audioPackets = 0;
  headerBytes = 0;
  /** Converts a granule to seconds. Set only for the stream that drives `frames`. */
  timeOf: ((g: number) => number) | null = null;
  constructor(readonly serial: number, readonly index: number) {}
}

interface Page {
  index: number;
  start: number;
  end: number;
  bodyStart: number;
  nseg: number;
  flags: number;
  granule: number; // -1 = no packet ends on this page
  seq: number;
  crcStored: number;
  crcCalc: number | null; // null when the page is truncated
  truncated: boolean;
  stream: Stream;
  /** Number given to the first packet that starts on this page. */
  firstNewPacket: number;
  /** Number of the packet continued from the previous page, or -1. */
  contNo: number;
  /** First bytes of the continued packet (needed to work out its duration). */
  contHead: Uint8Array | null;
  /** The page says it continues a packet, but we never saw the start. */
  orphan: boolean;
  completedAudio: number;
  seg: Segment;
}

/** One piece of a packet on one page, as found by walking the lacing table. */
interface PagePiece {
  s: number;
  e: number;
  complete: boolean;
  /** Lacing table indices [l0, l1) that describe this piece. */
  l0: number;
  l1: number;
  /** Packet number, or -1 for a piece whose packet start we never saw. */
  no: number;
  startsHere: boolean;
}


// ---------------------------------------------------------------------------
// Small helpers.

/** Reads the 64-bit granule. All-ones bytes mean -1 ("no packet ends here"). */
function granuleAt(b: Uint8Array, o: number): number {
  let allOnes = true;
  for (let i = 0; i < 8; i++) if (b[o + i] !== 0xff) allOnes = false;
  if (allOnes) return -1;
  return u32le(b, o + 4) * 4294967296 + u32le(b, o);
}

const s32le = (b: Uint8Array, o: number) => u32le(b, o) | 0;
const brText = (v: number) => (v > 0 ? `${fmtKbps(v)} (${v.toLocaleString('en-US')} bps)` : 'Not set');
const ilog = (v: number) => (v <= 0 ? 0 : 32 - Math.clz32(v));

function concat(a: Uint8Array, b: Uint8Array): Uint8Array {
  const out = new Uint8Array(a.length + b.length);
  out.set(a, 0);
  out.set(b, a.length);
  return out;
}

function b64decode(s: string): Uint8Array | null {
  try {
    const bin = atob(s.replace(/[^A-Za-z0-9+/=]/g, ''));
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
    return out;
  } catch {
    return null;
  }
}

/** Walks the lacing table of a page. Calls `cb` for each packet piece. */
function walkLacing(
  b: Uint8Array, p: number, nseg: number, bodyStart: number, end: number,
  cb: (s: number, e: number, complete: boolean, l0: number, l1: number) => void,
): void {
  let s = bodyStart, len = 0, l0 = 0;
  for (let i = 0; i < nseg; i++) {
    const v = b[p + 27 + i];
    len += v;
    if (v < 255) {
      const e = Math.min(s + len, end);
      cb(s, e, e === s + len, l0, i + 1);
      if (e < s + len) return; // truncated page
      s += len;
      len = 0;
      l0 = i + 1;
    }
  }
  if (len > 0) cb(s, Math.min(s + len, end), false, l0, nseg);
}

// ---------------------------------------------------------------------------
// Codec tables.

const OPUS_BW = ['Narrowband (4 kHz)', 'Mediumband (6 kHz)', 'Wideband (8 kHz)', 'Super-wideband (12 kHz)', 'Fullband (20 kHz)'];

/** Decodes an Opus TOC config number (0–31) into mode, bandwidth and frame size. */
export function opusConfig(cfg: number): { mode: string; bw: string; bwShort: string; samples: number } {
  if (cfg < 12) {
    const bw = cfg >> 2; // 0 NB, 1 MB, 2 WB
    return { mode: 'SILK-only', bw: OPUS_BW[bw], bwShort: ['NB', 'MB', 'WB'][bw], samples: [480, 960, 1920, 2880][cfg & 3] };
  }
  if (cfg < 16) {
    const swb = cfg < 14;
    return { mode: 'Hybrid', bw: OPUS_BW[swb ? 3 : 4], bwShort: swb ? 'SWB' : 'FB', samples: [480, 960][cfg & 1] };
  }
  const bw = (cfg - 16) >> 2; // 0 NB, 1 WB, 2 SWB, 3 FB
  return { mode: 'CELT-only', bw: OPUS_BW[[0, 2, 3, 4][bw]], bwShort: ['NB', 'WB', 'SWB', 'FB'][bw], samples: [120, 240, 480, 960][cfg & 3] };
}
const ms = (samples48k: number) => `${+(samples48k / 48).toFixed(1)} ms`;
const OPUS_CONFIGS = Array.from({ length: 32 }, (_, i) => opusConfig(i));
/** Short packet summaries per TOC config, precomputed because every packet needs one. */
const OPUS_SUMMARY = OPUS_CONFIGS.map((c) => `${c.mode} · ${c.bwShort} · ${ms(c.samples)} frames · `);
/** Fast byte-size text for hot paths. */
const quickBytes = (n: number) => (n < 1024 ? n + ' B' : fmtBytes(n));

/** Block size in samples from a FLAC frame header, or null if it can't be read. */
function flacFrameInfo(h: Uint8Array): { block: number; numLen: number; extra: number } | null {
  if (h.length < 5 || h[0] !== 0xff || (h[1] & 0xfe) !== 0xf8) return null;
  const bc = h[2] >> 4;
  const lead = Math.clz32(~(h[4] << 24)); // number of leading 1 bits in the first UTF-8 byte
  const numLen = lead === 0 ? 1 : lead;
  const at = 4 + numLen;
  if (bc === 1) return { block: 192, numLen, extra: 0 };
  if (bc >= 2 && bc <= 5) return { block: 576 << (bc - 2), numLen, extra: 0 };
  if (bc >= 8) return { block: 256 << (bc - 8), numLen, extra: 0 };
  if (bc === 6 && h.length > at) return { block: h[at] + 1, numLen, extra: 1 };
  if (bc === 7 && h.length > at + 1) return { block: u16be(h, at) + 1, numLen, extra: 2 };
  return null;
}

/**
 * Samples produced by one audio packet, in granule units, or null when we
 * can't tell. `st.prev` carries the Vorbis previous block size between calls.
 */
function packetDuration(s: Stream, buf: Uint8Array, off: number, end: number, st: { prev: number }): number | null {
  if (end <= off) return null;
  const h0 = buf[off];
  switch (s.codec) {
    case 'Opus': {
      const code = h0 & 3;
      const count = code === 0 ? 1 : code < 3 ? 2 : end > off + 1 ? buf[off + 1] & 0x3f : NaN;
      return Number.isNaN(count) ? null : OPUS_CONFIGS[h0 >> 3].samples * count;
    }
    case 'Vorbis': {
      if (!s.modeFlags || h0 & 1) return null;
      const mode = (h0 >> 1) & ((1 << s.modeBits) - 1);
      const flag = s.modeFlags[mode];
      if (flag === undefined) return null;
      const cur = s.blocksizes[flag];
      // Each block overlaps half of its neighbours, so a packet yields
      // prev/4 + cur/4 new samples. The very first packet yields none.
      const d = st.prev ? (st.prev + cur) / 4 : 0;
      st.prev = cur;
      return d;
    }
    case 'FLAC':
      return flacFrameInfo(buf.subarray(off, end))?.block ?? null;
    case 'Speex':
      return s.speexSamplesPerPacket || null;
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Header packet parsers. All work in packet coordinates: `buf` holds the whole
// packet, and offset 0 is its first byte.

function identify(head: Uint8Array): Codec {
  if (head[0] === 1 && matches(head, 1, 'vorbis')) return 'Vorbis';
  if (matches(head, 0, 'OpusHead')) return 'Opus';
  if (head[0] === 0x7f && matches(head, 1, 'FLAC')) return 'FLAC';
  if (matches(head, 0, 'Speex   ')) return 'Speex';
  if (head[0] === 0x80 && matches(head, 1, 'theora')) return 'Theora';
  if (matches(head, 0, 'fishead')) return 'Skeleton';
  return 'Unknown';
}

/** Adds the decoded details of METADATA_BLOCK_PICTURE comments; returns notes. */
function describeTextPictures(buf: Uint8Array, vc: VorbisCommentResult, notes: Note[]): void {
  for (const seg of vc.segments) {
    if (seg.type !== 'vorbis.comment.picture') continue;
    const valueStart = seg.start + 4 + 'METADATA_BLOCK_PICTURE='.length;
    if (valueStart >= seg.end) continue;
    const bin = b64decode(ascii(buf, valueStart, seg.end - valueStart));
    if (!bin || bin.length < 32) continue;
    try {
      const pic = parseFlacPicture(bin, 0);
      const f = pic.fields;
      const w = f[3].raw, h = f[4].raw;
      const size = pic.picture.end - pic.picture.start;
      const desc = `${pic.picture.mime || 'image'}, ${w} × ${h} px, ${pic.picture.pictureType ?? 'picture'}, ${fmtBytes(size)}`;
      const own = Array.isArray(seg.fields) ? seg.fields : [];
      seg.fields = [...own, {
        name: 'Decoded picture', value: desc, bit: valueStart * 8, bits: (seg.end - valueStart) * 8,
        note: 'We decoded the base64 text to read the FLAC picture header inside it.',
      }];
      seg.summary = `Base64 picture: ${desc}`;
      notes.push({
        level: 'info',
        text: `Cover art (${desc}) is stored as base64 text in a METADATA_BLOCK_PICTURE comment. Base64 makes it about 33% larger than the image file.`,
      });
    } catch {
      /* not a valid picture structure; leave the comment as plain text */
    }
  }
}

function vorbisHeaderFields(type: number, what: string): Field[] {
  return [
    field('Packet type', `${type} (${what})`, 0, 1, 'Vorbis header packets use odd types: 1, 3 and 5. Audio packets use even types.', type),
    field('Signature', '"vorbis"', 1, 6, 'Six ASCII letters that mark a Vorbis header.'),
  ];
}

function parseVorbisId(s: Stream, buf: Uint8Array): Detail {
  const ver = u32le(buf, 7), ch = buf[11], rate = u32le(buf, 12);
  const max = s32le(buf, 16), nom = s32le(buf, 20), min = s32le(buf, 24);
  const bs0 = 1 << (buf[28] & 15), bs1 = 1 << (buf[28] >> 4);
  Object.assign(s, { rate, channels: ch, nominal: nom, blocksizes: [bs0, bs1] });
  s.codecLong = 'Xiph.Org Vorbis';
  return {
    kind: 'header', type: 'ogg.vorbis.id', name: 'Vorbis identification header',
    summary: `${channelName(ch)} · ${fmtHz(rate)}${nom > 0 ? ` · ${fmtKbps(nom)} nominal` : ''}`,
    children: [],
    fields: [
      ...vorbisHeaderFields(1, 'identification'),
      field('Vorbis version', ver, 7, 4, 'Always 0. A player must reject any other value.'),
      field('Channels', `${ch} (${channelName(ch)})`, 11, 1, 'Number of audio channels.', ch),
      field('Sample rate', fmtHz(rate), 12, 4, 'Samples per second, per channel.', rate),
      field('Maximum bitrate', brText(max), 16, 4, 'A hint for streaming. 0 means the encoder did not set it.', max),
      field('Nominal bitrate', brText(nom), 20, 4, 'The average the encoder aimed for. The real rate can differ.', nom),
      field('Minimum bitrate', brText(min), 24, 4, 'A hint for streaming. 0 means the encoder did not set it.', min),
      { name: 'Long block size', value: `${bs1} samples`, raw: buf[28] >> 4, bit: 28 * 8, bits: 4, note: 'Stored as a power of two. Long blocks give better frequency detail for steady sounds.' },
      { name: 'Short block size', value: `${bs0} samples`, raw: buf[28] & 15, bit: 28 * 8 + 4, bits: 4, note: 'Stored as a power of two. Short blocks keep sharp attacks crisp.' },
      { name: 'Framing bit', value: String(buf[29] & 1), raw: buf[29] & 1, bit: 29 * 8 + 7, bits: 1, note: 'Must be 1. It is a simple check that the header was read correctly.' },
    ],
  };
}

function parseVorbisCommentPacket(s: Stream, buf: Uint8Array): Detail {
  const vc = parseVorbisComment(buf, 7, buf.length);
  s.vendor = vc.vendor;
  s.tags.push(...vc.tags);
  describeTextPictures(buf, vc, s.notes);
  const children = [...vc.segments];
  if (vc.end < buf.length)
    children.push({
      kind: 'header', type: 'ogg.vorbis.framing', name: 'Framing bit', start: vc.end, end: vc.end + 1,
      fields: [{ name: 'Framing bit', value: String(buf[vc.end] & 1), raw: buf[vc.end] & 1, bit: vc.end * 8 + 7, bits: 1, note: 'Must be 1. It marks the end of the comment header.' }],
    });
  return {
    kind: 'metadata', type: 'ogg.vorbis.comment', name: 'Vorbis comment header', role: 'comment',
    summary: `${plural(vc.tags.length, 'tag')} · ${vc.vendor}`,
    fields: [...vorbisHeaderFields(3, 'comment'), ...vc.fields], children,
  };
}

/**
 * The setup header holds codebooks, floors, residues, mappings and modes. We
 * only need the mode list (to know each audio packet's block size), and it sits
 * at the very end. Like ffmpeg and liboggz, we read the packet backwards from
 * its framing bit: each mode is 41 bits (1 block flag, 16 + 16 zero bits, an
 * 8-bit mapping number), preceded by a 6-bit "mode count - 1".
 */
function parseVorbisSetup(s: Stream, buf: Uint8Array): Detail {
  const fields = vorbisHeaderFields(5, 'setup');
  const books = buf[7] + 1;
  fields.push(field('Codebook count', books, 7, 1, 'Stored as count − 1. Codebooks are the Huffman-style tables that the audio packets use.', books));
  const n = buf.length, total = n * 8;
  // Bit k of the backward stream; Vorbis packs bits LSB first, so reading the
  // reversed bytes MSB first walks the bitstream backwards.
  const bitAt = (k: number) => (buf[n - 1 - (k >> 3)] >> (7 - (k & 7))) & 1;
  const origBit = (k: number) => (n - 1 - (k >> 3)) * 8 + (k & 7); // position as an MSB-first Field bit
  const read = (k: number, len: number) => { let v = 0; for (let i = 0; i < len; i++) v = v * 2 + bitAt(k + i); return v; };
  let k = 0, framing = -1;
  while (total - k > 97) if (bitAt(k++)) { framing = k; break; }
  let found = 0;
  if (framing >= 0) {
    for (let c = 1; c <= 64 && total - k >= 97; c++) {
      if (read(k, 8) > 63 || read(k + 8, 16) || read(k + 24, 16)) break;
      k += 41;
      if (read(k, 6) + 1 === c) found = c;
    }
  }
  let summary = `${plural(books, 'codebook')}`;
  if (found > 0) {
    const flags: number[] = new Array(found);
    let q = framing;
    for (let i = found - 1; i >= 0; i--) {
      q += 40;
      flags[i] = bitAt(q);
      fields.push({
        name: `Mode ${i} block flag`, value: flags[i] ? `1 (long, ${s.blocksizes[1]} samples)` : `0 (short, ${s.blocksizes[0]} samples)`,
        raw: flags[i], bit: origBit(q), bits: 1, note: 'Says whether audio packets that use this mode are long or short blocks.',
      });
      q += 1;
    }
    const lo = Math.min(...[0, 1, 2, 3, 4, 5].map((i) => origBit(q + i))), hi = Math.max(...[0, 1, 2, 3, 4, 5].map((i) => origBit(q + i)));
    fields.push({ name: 'Mode count', value: String(found), raw: found - 1, bit: lo, bits: hi - lo + 1, note: 'Stored as count − 1 in 6 bits. Each audio packet names one of these modes.' });
    fields.push({ name: 'Framing bit', value: '1', raw: 1, bit: origBit(framing - 1), bits: 1, note: 'Must be 1. It marks the end of the setup header.' });
    s.modeFlags = flags;
    s.modeBits = ilog(found - 1);
    summary += ` · ${plural(found, 'mode')}`;
  } else {
    s.durKnown = false;
  }
  return { kind: 'header', type: 'ogg.vorbis.setup', name: 'Vorbis setup header', summary, fields, children: [] };
}

/**
 * Block flag (0 = short, 1 = long) of each mode in a Vorbis setup header
 * packet, or null when the modes cannot be found. For codec analysis.
 */
export function vorbisModeFlags(setup: Uint8Array): number[] | null {
  const s = new Stream(0, 0);
  parseVorbisSetup(s, setup);
  return s.modeFlags;
}

function parseOpusHead(s: Stream, buf: Uint8Array): Detail {
  const ver = buf[8], ch = buf[9], preSkip = u16le(buf, 10), inRate = u32le(buf, 12), gain = i16le(buf, 16), family = buf[18];
  Object.assign(s, { channels: ch, preSkip, inputRate: inRate, rate: 48000 });
  s.codecLong = 'Opus (IETF RFC 6716)';
  const fields: Field[] = [
    field('Signature', '"OpusHead"', 0, 8, 'Eight ASCII letters that mark the Opus identification header.'),
    field('Version', ver, 8, 1, 'Always 1 today. Players accept any version below 16.'),
    field('Channels', `${ch} (${channelName(ch)})`, 9, 1, 'Number of output channels.', ch),
    field('Pre-skip', `${preSkip} samples (${ms(preSkip)})`, 10, 2, 'Samples at 48 kHz that the player must drop at the start. The encoder needs them to warm up.', preSkip),
    field('Input sample rate', inRate ? fmtHz(inRate) : 'Not set', 12, 4, 'The rate of the original audio. It is only a hint; Opus always decodes at 48 kHz.', inRate),
    field('Output gain', `${(gain / 256).toFixed(2)} dB`, 16, 2, 'Volume change to apply on playback, in Q7.8 fixed point (value ÷ 256 = dB).', gain),
    field('Channel mapping family', `${family} (${['Mono/stereo', 'Vorbis channel order (surround)'][family] ?? (family === 255 ? 'Undefined order' : 'Other')})`, 18, 1, 'Says how the coded streams map to speakers.', family),
  ];
  if (family !== 0 && buf.length >= 21 + ch) {
    fields.push(field('Stream count', buf[19], 19, 1, 'How many Opus streams each packet holds.'));
    fields.push(field('Coupled stream count', buf[20], 20, 1, 'How many of those streams are stereo pairs.'));
    fields.push(field('Channel mapping', Array.from(buf.subarray(21, 21 + ch)).join(', '), 21, ch, 'For each output channel, the decoded channel it comes from.'));
  }
  return {
    kind: 'header', type: 'ogg.opus.head', name: 'OpusHead (identification header)',
    summary: `${channelName(ch)} · pre-skip ${preSkip} · input ${inRate ? fmtHz(inRate) : 'unknown'}`, fields, children: [],
  };
}

/** Parses a Vorbis comment list at `at` and stores tags; shared by OpusTags, Speex and FLAC blocks. */
function commentChildren(s: Stream, buf: Uint8Array, at: number): { vc: VorbisCommentResult; children: Segment[] } {
  const vc = parseVorbisComment(buf, at, buf.length);
  s.vendor = vc.vendor;
  s.tags.push(...vc.tags);
  describeTextPictures(buf, vc, s.notes);
  return { vc, children: [...vc.segments] };
}

function parseOpusTags(s: Stream, buf: Uint8Array): Detail {
  const { vc, children } = commentChildren(s, buf, 8);
  if (vc.end < buf.length)
    children.push({
      kind: 'padding', type: 'ogg.opus.tagsExtra', name: 'Extra data', start: vc.end, end: buf.length, summary: fmtBytes(buf.length - vc.end),
      fields: [field('First byte', hex(buf[vc.end]), vc.end, 1, 'If the lowest bit is 1, the bytes are data to keep; if 0, they are padding.')],
    });
  return {
    kind: 'metadata', type: 'ogg.opus.tags', name: 'OpusTags (comment header)', role: 'comment',
    summary: `${plural(vc.tags.length, 'tag')} · ${vc.vendor}`,
    fields: [field('Signature', '"OpusTags"', 0, 8, 'Eight ASCII letters that mark the Opus comment header.'), ...vc.fields], children,
  };
}

const FLAC_BLOCKS = ['STREAMINFO', 'PADDING', 'APPLICATION', 'SEEKTABLE', 'VORBIS_COMMENT', 'CUESHEET', 'PICTURE'];

function parseFlacHead(s: Stream, buf: Uint8Array): Detail {
  const nHeaders = u16be(buf, 7);
  // 0 means "unknown": we then watch for the last-block flag instead.
  s.headerCount = nHeaders ? 1 + nHeaders : Infinity;
  const bf = new BitFields(buf, 0);
  bf.read('Packet type', 8, (v) => hex(v), 'Always 0x7F for the Ogg FLAC mapping header.');
  bf.fields.push(field('Signature', '"FLAC"', 1, 4, 'Four ASCII letters that mark FLAC inside Ogg.'));
  bf.skip(32);
  bf.read('Mapping version', 8, (v) => `${v}.${buf[6]}`, 'Version of the Ogg FLAC mapping. 1.0 is the only one in use.');
  bf.skip(8);
  bf.read('Header packets', 16, (v) => (v ? String(v) : '0 (unknown)'), 'How many metadata packets follow this one before the audio.');
  bf.fields.push(field('Native signature', '"fLaC"', 9, 4, 'The normal FLAC file marker, kept so the rest looks like a FLAC file.'));
  bf.skip(32);
  bf.read('Last block', 1, (v) => (v ? 'Yes' : 'No'), 'Set on the last metadata block.');
  bf.read('Block type', 7, (v) => `${v} (${FLAC_BLOCKS[v] ?? 'Reserved'})`, 'Always 0 here: STREAMINFO.');
  bf.read('Block length', 24, (v) => `${v} bytes`, 'STREAMINFO is always 34 bytes.');
  s.flacMinBlock = bf.read('Min block size', 16, (v) => `${v} samples`, 'Smallest number of samples in one frame.');
  s.flacMaxBlock = bf.read('Max block size', 16, (v) => `${v} samples`, 'Largest number of samples in one frame.');
  bf.read('Min frame size', 24, (v) => (v ? `${v} bytes` : 'Unknown'), 'Smallest frame in bytes; 0 means unknown.');
  bf.read('Max frame size', 24, (v) => (v ? `${v} bytes` : 'Unknown'), 'Largest frame in bytes; 0 means unknown.');
  s.rate = bf.read('Sample rate', 20, fmtHz, 'Samples per second, per channel.');
  s.channels = bf.read('Channels', 3, (v) => `${v + 1} (${channelName(v + 1)})`, 'Stored as channels − 1.') + 1;
  s.bitsPerSample = bf.read('Bits per sample', 5, (v) => `${v + 1}-bit`, 'Stored as bits − 1.') + 1;
  s.totalSamples = bf.read('Total samples', 36, (v) => (v ? v.toLocaleString('en-US') : 'Unknown'), 'Samples per channel in the whole stream; 0 means unknown.');
  const md5 = Array.from(buf.subarray(bf.byte, bf.byte + 16), (x) => x.toString(16).padStart(2, '0')).join('');
  bf.fields.push(field('MD5 of audio', md5, bf.byte, 16, 'A fingerprint of the decoded audio, so a decoder can prove the result is bit-exact.'));
  s.codecLong = 'Free Lossless Audio Codec in Ogg';
  return {
    kind: 'header', type: 'ogg.flac.head', name: 'Ogg FLAC header + STREAMINFO',
    summary: `${channelName(s.channels)} · ${fmtHz(s.rate)} · ${s.bitsPerSample}-bit`, fields: bf.fields, children: [],
  };
}

function parseFlacMetadata(s: Stream, no: number, buf: Uint8Array): Detail {
  const last = buf[0] >> 7, type = buf[0] & 0x7f, len = (buf[1] << 16) | (buf[2] << 8) | buf[3];
  if (last && s.headerCount === Infinity) s.headerCount = no + 1;
  const name = FLAC_BLOCKS[type] ?? `Block type ${type}`;
  const fields: Field[] = [
    { name: 'Last block', value: last ? 'Yes' : 'No', raw: last, bit: 0, bits: 1, note: 'Set on the last metadata block before the audio.' },
    { name: 'Block type', value: `${type} (${name})`, raw: type, bit: 1, bits: 7, note: 'What kind of metadata block this is.' },
    field('Block length', `${len} bytes`, 1, 3, 'Size of the block data that follows.', len),
  ];
  const d: Detail = { kind: 'header', type: 'ogg.flac.metadata', name: `${name} block`, summary: fmtBytes(len), fields, children: [] };
  if (type === 4) {
    const { vc, children } = commentChildren(s, buf, 4);
    Object.assign(d, { kind: 'metadata', role: 'comment', children, summary: `${plural(vc.tags.length, 'tag')} · ${vc.vendor}` });
    fields.push(...vc.fields);
  } else if (type === 6) {
    const pic = parseFlacPicture(buf, 4);
    Object.assign(d, { kind: 'metadata', role: 'comment', children: pic.children, picture: pic.picture, summary: `${pic.picture.mime} · ${fmtBytes(pic.picture.end - pic.picture.start)}` });
  } else if (type === 1) d.kind = 'padding';
  else if (type === 3) d.kind = 'index';
  return d;
}

function parseSpeexHead(s: Stream, buf: Uint8Array): Detail {
  const ver = ascii(buf, 8, 20).replace(/\0.*$/, '');
  const rate = u32le(buf, 36), mode = u32le(buf, 40), ch = u32le(buf, 48), br = s32le(buf, 52);
  const frame = u32le(buf, 56), vbr = u32le(buf, 60), fpp = u32le(buf, 64), extra = u32le(buf, 68);
  Object.assign(s, { rate, channels: ch, headerCount: 2 + extra, speexSamplesPerPacket: frame * Math.max(1, fpp) });
  s.codecLong = 'Speex speech codec';
  return {
    kind: 'header', type: 'ogg.speex.head', name: 'Speex header',
    summary: `${channelName(ch)} · ${fmtHz(rate)} · ${['narrowband', 'wideband', 'ultra-wideband'][mode] ?? 'mode ' + mode}`, children: [],
    fields: [
      field('Signature', '"Speex   "', 0, 8, 'Marks a Speex stream.'),
      field('Encoder version', ver, 8, 20, 'Text that names the Speex version that wrote the file.'),
      field('Version ID', u32le(buf, 28), 28, 4, 'Numeric header version.'),
      field('Header size', u32le(buf, 32), 32, 4, 'Size of this header in bytes.'),
      field('Sample rate', fmtHz(rate), 36, 4, 'Samples per second.', rate),
      field('Mode', `${mode} (${['Narrowband', 'Wideband', 'Ultra-wideband'][mode] ?? '?'})`, 40, 4, 'Speex has three modes for 8, 16 and 32 kHz audio.', mode),
      field('Mode bitstream version', u32le(buf, 44), 44, 4, 'Version of the coded format for this mode.'),
      field('Channels', ch, 48, 4, 'Number of channels.'),
      field('Bitrate', br > 0 ? fmtKbps(br) : 'Not set', 52, 4, 'Target bitrate; -1 means not set.', br),
      field('Frame size', `${frame} samples`, 56, 4, 'Samples in one Speex frame.', frame),
      field('VBR', vbr ? 'Yes' : 'No', 60, 4, 'Whether the encoder used variable bitrate.', vbr),
      field('Frames per packet', fpp, 64, 4, 'How many Speex frames each Ogg packet holds.'),
      field('Extra headers', extra, 68, 4, 'Extra header packets after the comments.'),
    ],
  };
}

/** Parses a completed header packet (whole bytes in `buf`). Updates the stream. */
function parseHeader(s: Stream, no: number, buf: Uint8Array): Detail | null {
  try {
    switch (s.codec) {
      case 'Vorbis':
        if (no === 0 && buf.length >= 30) return parseVorbisId(s, buf);
        if (no === 1 && buf[0] === 3) return parseVorbisCommentPacket(s, buf);
        if (no === 2 && buf[0] === 5) return parseVorbisSetup(s, buf);
        break;
      case 'Opus':
        if (no === 0 && buf.length >= 19) return parseOpusHead(s, buf);
        if (no === 1 && matches(buf, 0, 'OpusTags')) return parseOpusTags(s, buf);
        break;
      case 'FLAC':
        if (no === 0 && buf.length >= 51) return parseFlacHead(s, buf);
        if (no > 0 && buf.length >= 4) return parseFlacMetadata(s, no, buf);
        break;
      case 'Speex':
        if (no === 0 && buf.length >= 72) return parseSpeexHead(s, buf);
        if (no === 1) {
          const { vc, children } = commentChildren(s, buf, 0);
          return { kind: 'metadata', type: 'ogg.speex.comment', name: 'Speex comment header', role: 'comment', summary: `${plural(vc.tags.length, 'tag')} · ${vc.vendor}`, fields: vc.fields, children };
        }
        return { kind: 'header', type: 'ogg.packet', name: 'Speex extra header', fields: [], children: [] };
      default:
        if (no === 0)
          return {
            kind: 'header', type: 'ogg.packet', name: `${s.codec === 'Unknown' ? 'Unknown codec' : s.codec} header`,
            summary: JSON.stringify(ascii(buf, 0, 8)),
            fields: [field('First bytes', Array.from(buf.subarray(0, 8), (x) => x.toString(16).padStart(2, '0')).join(' '), 0, Math.min(8, buf.length), 'The first packet of a stream names its codec. We do not know this one.')],
            children: [],
          };
    }
  } catch {
    /* a damaged header: fall through to "no detail" */
  }
  return null;
}

// ---------------------------------------------------------------------------
// Moving packet-coordinate details onto the file bytes of one piece.

function shiftFields(fields: Field[], lo: number, hi: number, delta: number): Field[] {
  const out: Field[] = [];
  for (const f of fields) {
    const bs = Math.floor(f.bit / 8), be = Math.ceil((f.bit + f.bits) / 8);
    if (bs >= lo && be <= hi) out.push({ ...f, bit: f.bit + delta * 8 });
  }
  return out;
}

/** Clips packet-coordinate segments to [lo, hi) and shifts them to file offsets. */
function clipSegments(list: Segment[], lo: number, hi: number, delta: number): Segment[] {
  const out: Segment[] = [];
  for (const seg of list) {
    const s0 = Math.max(seg.start, lo), e0 = Math.min(seg.end, hi);
    if (e0 <= s0) continue;
    const clipped = s0 !== seg.start || e0 !== seg.end;
    const kids = typeof seg.children === 'function' ? seg.children() : seg.children ?? [];
    const flds = typeof seg.fields === 'function' ? seg.fields() : seg.fields ?? [];
    out.push({
      ...seg,
      name: clipped ? `${seg.name} (part)` : seg.name,
      start: s0 + delta, end: e0 + delta,
      fields: shiftFields(flds, s0, e0, delta),
      children: clipSegments(kids, s0, e0, delta),
      image: seg.image && !clipped ? { ...seg.image, start: seg.image.start + delta, end: seg.image.end + delta } : undefined,
    });
  }
  return out;
}

// ---------------------------------------------------------------------------
// Per-packet detail for audio packets (built lazily).

function opusPacketFields(b: Uint8Array, s: number, e: number): { fields: Field[]; summary: string } {
  const bf = new BitFields(b, s);
  const cfgNo = b[s] >> 3;
  const cfg = opusConfig(cfgNo);
  bf.read('Config', 5, (v) => `${v} (${cfg.mode}, ${cfg.bwShort}, ${ms(cfg.samples)})`, 'One of 32 presets. It sets the mode, the bandwidth and the frame length.');
  const at = s * 8;
  bf.fields.push(
    { name: 'Mode', value: cfg.mode, raw: cfgNo, bit: at, bits: 5, note: 'SILK is a speech coder, CELT is a music coder, and Hybrid uses both at once.' },
    { name: 'Audio bandwidth', value: cfg.bw, raw: cfgNo, bit: at, bits: 5, note: 'The highest audio frequency the packet keeps.' },
    { name: 'Frame duration', value: ms(cfg.samples), raw: cfg.samples, bit: at, bits: 5, note: 'Length of each coded frame. Short frames lower delay; long frames save bits.' },
  );
  const stereo = bf.read('Stereo', 1, (v) => (v ? 'Stereo' : 'Mono'), 'Whether this packet codes two channels or one.');
  const code = bf.read('Frame count code', 2, (v) => ['0: one frame', '1: two frames, same size', '2: two frames, different sizes', '3: any number of frames'][v], 'How many frames the packet holds and how their sizes are stored.');
  let frames = code === 0 ? 1 : 2;
  if (code === 3 && s + 1 < e) {
    bf.read('VBR', 1, (v) => (v ? 'Yes: frames differ in size' : 'No: frames are the same size'), 'Whether each frame has its own size.');
    bf.read('Padding', 1, (v) => (v ? 'Yes' : 'No'), 'Whether padding bytes follow. Padding can hide the true size of speech.');
    frames = bf.read('Frame count', 6, (v) => String(v), 'Number of frames in this packet (1–48).');
  } else if (code === 2 && s + 1 < e) {
    const two = b[s + 1] >= 252 && s + 2 < e;
    const size = two ? b[s + 2] * 4 + b[s + 1] : b[s + 1];
    bf.fields.push(field('First frame size', `${size} bytes`, s + 1, two ? 2 : 1, 'The second frame gets the rest of the packet.', size));
  }
  return {
    fields: bf.fields,
    summary: `${cfg.mode} · ${cfg.bwShort} · ${plural(frames, 'frame')} × ${ms(cfg.samples)} · ${stereo ? 'stereo' : 'mono'} · ${fmtBytes(e - s)}`,
  };
}

function vorbisPacketFields(st: Stream, b: Uint8Array, s: number, e: number): { fields: Field[]; summary: string } {
  const byte = b[s];
  const fields: Field[] = [{
    name: 'Packet type', value: byte & 1 ? '1 (not audio!)' : '0 (audio)', raw: byte & 1, bit: s * 8 + 7, bits: 1,
    note: 'The lowest bit of the first byte. 0 marks an audio packet.',
  }];
  if (!st.modeFlags) return { fields, summary: fmtBytes(e - s) };
  const mb = st.modeBits;
  const mode = (byte >> 1) & ((1 << mb) - 1);
  const long = st.modeFlags[mode] === 1;
  const size = st.blocksizes[long ? 1 : 0];
  // Vorbis packs bits from the lowest bit up, so the mode number sits just
  // above the type bit. In MSB-first bit numbering that is bits 7-mb .. 6.
  if (mb > 0)
    fields.push({
      name: 'Mode', value: `${mode} (${long ? 'long' : 'short'} block, ${size} samples)`, raw: mode, bit: s * 8 + 7 - mb, bits: mb,
      note: `An index into the ${st.modeFlags.length} modes from the setup header. The mode sets the block size.`,
    });
  if (long && mb + 3 <= 8) {
    fields.push({ name: 'Previous window', value: byte & (1 << (mb + 1)) ? 'Long' : 'Short', raw: (byte >> (mb + 1)) & 1, bit: s * 8 + 6 - mb, bits: 1, note: 'Shape of the overlap with the block before this one.' });
    fields.push({ name: 'Next window', value: byte & (1 << (mb + 2)) ? 'Long' : 'Short', raw: (byte >> (mb + 2)) & 1, bit: s * 8 + 5 - mb, bits: 1, note: 'Shape of the overlap with the block after this one.' });
  }
  return { fields, summary: `${long ? 'Long' : 'Short'} block (${size} samples) · ${fmtBytes(e - s)}` };
}

const FLAC_RATES = ['From STREAMINFO', '88.2 kHz', '176.4 kHz', '192 kHz', '8 kHz', '16 kHz', '22.05 kHz', '24 kHz', '32 kHz', '44.1 kHz', '48 kHz', '96 kHz', '8-bit value after header', '16-bit value after header', '16-bit value after header', 'Invalid'];
const FLAC_SIZES = ['From STREAMINFO', '8-bit', '12-bit', 'Reserved', '16-bit', '20-bit', '24-bit', '32-bit'];
const FLAC_CH = ['Mono', 'Left/right', '3 ch', '4 ch', '5 ch', '6 ch', '7 ch', '8 ch', 'Left/side', 'Right/side', 'Mid/side'];

function flacPacketFields(b: Uint8Array, s: number, e: number): { fields: Field[]; summary: string } {
  const info = flacFrameInfo(b.subarray(s, Math.min(e, s + 16)));
  if (!info) return { fields: [], summary: fmtBytes(e - s) };
  const bf = new BitFields(b, s);
  bf.read('Sync code', 14, (v) => `0b${v.toString(2)}`, 'Fourteen bits 11111111111110 mark the start of a FLAC frame.');
  bf.read('Reserved', 1, String, 'Must be 0.');
  const variable = bf.read('Blocking strategy', 1, (v) => (v ? 'Variable block size' : 'Fixed block size'), 'Says whether the next number counts frames or samples.');
  bf.read('Block size code', 4, () => `${info.block} samples`, 'Samples per channel in this frame, via a lookup table.');
  bf.read('Sample rate', 4, (v) => FLAC_RATES[v], 'A 4-bit code for the sample rate, so a decoder can start mid-stream.');
  bf.read('Channel assignment', 4, (v) => FLAC_CH[v] ?? 'Reserved', 'How the channels are coded. Stereo files often use side channels to save space.');
  bf.read('Sample size', 3, (v) => FLAC_SIZES[v], 'A 3-bit code for the bit depth.');
  bf.read('Reserved', 1, String, 'Must be 0.');
  // The frame (or sample) number is coded like a UTF-8 character: the first
  // byte's leading 1 bits give the length, then 6 bits per extra byte.
  let num = info.numLen === 1 ? b[s + 4] : b[s + 4] & (0x7f >> info.numLen);
  for (let i = 1; i < info.numLen; i++) num = num * 64 + (b[s + 4 + i] & 0x3f);
  bf.fields.push(field(variable ? 'Sample number' : 'Frame number', `${num.toLocaleString('en-US')} (UTF-8 coded, ${plural(info.numLen, 'byte')})`, s + 4, info.numLen, 'The position of this frame, coded like a UTF-8 character.', num));
  if (info.extra) bf.fields.push(field('Block size', `${info.block} samples`, s + 4 + info.numLen, info.extra, 'Stored as size − 1.'));
  return { fields: bf.fields, summary: `${info.block.toLocaleString('en-US')} samples · ${fmtBytes(e - s)}` };
}

// ---------------------------------------------------------------------------
// The parser.

function pagePieces(b: Uint8Array, pg: Page): PagePiece[] {
  const out: PagePiece[] = [];
  let next = pg.firstNewPacket;
  let first = true;
  walkLacing(b, pg.start, pg.nseg, pg.bodyStart, pg.end, (s, e, complete, l0, l1) => {
    if (first && pg.flags & 1) out.push({ s, e, complete, l0, l1, no: pg.orphan ? -1 : pg.contNo, startsHere: false });
    else out.push({ s, e, complete, l0, l1, no: next++, startsHere: true });
    first = false;
  });
  return out;
}

function pageHeaderSegment(b: Uint8Array, pg: Page, pieces: PagePiece[]): Segment {
  const p = pg.start, n = pg.nseg;
  const st = pg.stream;
  return {
    kind: 'header', type: 'ogg.pageHeader', name: 'Page header', start: p, end: pg.bodyStart,
    summary: `${plural(n, 'lacing value')} · ${pg.granule === -1 ? 'no granule' : `granule ${pg.granule.toLocaleString('en-US')}`}`,
    warning: pg.crcCalc !== null && pg.crcCalc !== pg.crcStored ? 'CRC mismatch' : undefined,
    fields: () => {
      const f: Field[] = [
        field('Capture pattern', '"OggS"', p, 4, 'Every page starts with these 4 bytes, so a reader can find pages again after damage.'),
        field('Version', b[p + 4], p + 4, 1, 'Always 0.'),
        field('Header type', `${hex(pg.flags)}`, p + 5, 1, 'Three flag bits that describe this page.', pg.flags),
        { name: 'End of stream (EOS)', value: pg.flags & 4 ? 'Yes' : 'No', raw: (pg.flags >> 2) & 1, bit: (p + 5) * 8 + 5, bits: 1, note: 'Set on the last page of a logical stream.' },
        { name: 'Beginning of stream (BOS)', value: pg.flags & 2 ? 'Yes' : 'No', raw: (pg.flags >> 1) & 1, bit: (p + 5) * 8 + 6, bits: 1, note: 'Set on the first page of a logical stream.' },
        { name: 'Continued packet', value: pg.flags & 1 ? 'Yes' : 'No', raw: pg.flags & 1, bit: (p + 5) * 8 + 7, bits: 1, note: 'Set when the first bytes of this page finish a packet from the previous page.' },
      ];
      let gv: string;
      if (pg.granule === -1) gv = '-1 (no packet ends on this page)';
      else {
        gv = pg.granule.toLocaleString('en-US');
        if (st.timeOf) gv += ` (${fmtSec(st.timeOf(pg.granule))})`;
      }
      f.push(field('Granule position', gv, p + 6, 8, st.codec === 'Opus'
        ? 'Samples at 48 kHz up to the end of the last packet that ends on this page (includes pre-skip).'
        : 'Samples per channel up to the end of the last packet that ends on this page.', pg.granule));
      f.push(field('Bitstream serial', hex(u32le(b, p + 14), 8), p + 14, 4, 'A random number that names the logical stream this page belongs to.', u32le(b, p + 14)));
      f.push(field('Page sequence', pg.seq, p + 18, 4, 'Counts pages in this stream from 0. A gap means a lost page.'));
      const crcText = pg.crcCalc === null ? `${hex(pg.crcStored, 8)} (page is cut off; not checked)`
        : pg.crcCalc === pg.crcStored ? `${hex(pg.crcStored, 8)} ✓ matches` : `${hex(pg.crcStored, 8)} ✗ computed ${hex(pg.crcCalc, 8)}`;
      f.push(field('CRC-32', crcText, p + 22, 4, 'Checksum of the whole page, computed with this field set to zero.', pg.crcStored));
      f.push(field('Segment count', n, p + 26, 1, 'Number of lacing values that follow (0–255).'));
      if (n > 0) {
        const vals = Array.from(b.subarray(p + 27, p + 27 + Math.min(n, 12))).join(', ') + (n > 12 ? ', …' : '');
        f.push(field('Lacing table', vals, p + 27, n, 'One byte per 255-byte segment. A value below 255 ends a packet.'));
      }
      for (const pc of pieces) {
        const label = pc.no < 0 ? 'Lost packet piece' : `Packet ${pc.no}`;
        // Show the lacing run as a sum, e.g. "= 3 × 255 + 17".
        const full = pc.l1 - pc.l0 - (pc.complete ? 1 : 0);
        const parts = [full === 1 ? '255' : full > 1 ? `${full} × 255` : '', pc.complete && full > 0 ? String(b[p + 27 + pc.l1 - 1]) : ''].filter(Boolean);
        const sum = parts.length > 1 || full > 1 ? ` = ${parts.join(' + ')}` : '';
        const size = pc.e - pc.s;
        f.push(field(`${label} size`, `${size.toLocaleString('en-US')} bytes${sum}${pc.complete ? '' : ' (continues)'}`, p + 27 + pc.l0, pc.l1 - pc.l0,
          pc.complete ? 'Lacing values add up to the packet size; the value below 255 ends it.' : 'All values are 255, so the packet goes on in the next page.', size));
      }
      return f;
    },
  };
}

function pieceName(base: string, pc: PagePiece): string {
  const cont = !pc.startsHere, more = !pc.complete;
  if (cont && more) return `${base} (continued, continues on next page)`;
  if (cont) return `${base} (continued)`;
  if (more) return `${base} (continues on next page)`;
  return base;
}

/** Quick one-line summary for an audio packet (cheap: built for every packet). */
function packetSummary(st: Stream, b: Uint8Array, pc: PagePiece): string {
  const size = quickBytes(pc.e - pc.s);
  if (!pc.startsHere) return size;
  if (st.codec === 'Opus') return OPUS_SUMMARY[b[pc.s] >> 3] + size;
  if (st.codec === 'Vorbis' && st.modeFlags) {
    const long = st.modeFlags[(b[pc.s] >> 1) & ((1 << st.modeBits) - 1)] === 1;
    return `${long ? 'Long' : 'Short'} block · ${size}`;
  }
  return size;
}

/**
 * Builds the segments for the packet pieces on a page. Audio pieces get lazy
 * fields but no time; the caller adds time for the stream that drives `frames`.
 * `audio` receives each audio piece (or lost piece) in byte order.
 */
function pieceSegments(b: Uint8Array, pg: Page, pieces: PagePiece[], audio?: (seg: Segment, pc: PagePiece) => void): Segment[] {
  const st = pg.stream;
  const out: Segment[] = [];
  for (const pc of pieces) {
    if (pc.no < 0) {
      const seg: Segment = {
        kind: AUDIO_CODECS.has(st.codec) ? 'audio' : 'unknown', type: 'ogg.packet', name: 'Packet piece (start lost)', start: pc.s, end: pc.e,
        summary: fmtBytes(pc.e - pc.s), warning: 'This page continues a packet, but the page with its start is missing.',
      };
      out.push(seg);
      if (seg.kind === 'audio') audio?.(seg, pc);
      continue;
    }
    const hp = pc.no < st.headerCount ? st.headers.get(pc.no) : undefined;
    if (hp) {
      const piece = hp.pieces.find((x) => x.fileStart === pc.s);
      const d = hp.detail;
      if (piece && d) {
        const lo = piece.logical, hi = lo + (piece.fileEnd - piece.fileStart), delta = piece.fileStart - piece.logical;
        out.push({
          kind: d.kind, type: d.type, name: pieceName(d.name, pc), start: pc.s, end: pc.e, summary: d.summary,
          fields: () => shiftFields(d.fields, lo, hi, delta), children: () => clipSegments(d.children, lo, hi, delta),
        });
      } else {
        out.push({ kind: 'header', type: 'ogg.packet', name: pieceName(`Packet ${pc.no}`, pc), start: pc.s, end: pc.e, summary: fmtBytes(pc.e - pc.s), warning: d ? undefined : 'This header packet could not be read.' });
      }
      continue;
    }
    if (pc.no < st.headerCount || !AUDIO_CODECS.has(st.codec)) {
      out.push({ kind: st.codec === 'Unknown' ? 'unknown' : 'audio', type: 'ogg.packet', name: pieceName(`Packet ${pc.no}`, pc), start: pc.s, end: pc.e, summary: fmtBytes(pc.e - pc.s) });
      continue;
    }
    // An audio packet (or a piece of one). Its fields come from its first bytes.
    const seg: Segment = { kind: 'audio', type: `ogg.${st.codec.toLowerCase()}.audio`, name: pieceName(`Packet ${pc.no}`, pc), start: pc.s, end: pc.e, summary: packetSummary(st, b, pc) };
    if (pc.startsHere) {
      const { s, e } = pc;
      seg.fields = () => (st.codec === 'Opus' ? opusPacketFields(b, s, e).fields
        : st.codec === 'Vorbis' ? vorbisPacketFields(st, b, s, e).fields
        : st.codec === 'FLAC' ? flacPacketFields(b, s, e).fields : []);
    }
    out.push(seg);
    audio?.(seg, pc);
  }
  return out;
}

function pageChildren(b: Uint8Array, pg: Page): Segment[] {
  const pieces = pagePieces(b, pg);
  return [pageHeaderSegment(b, pg, pieces), ...pieceSegments(b, pg, pieces)];
}

/**
 * Builds the packet segments of the first audio stream eagerly and gives each
 * packet piece a time. These pieces are the `frames` of the file.
 *
 * Each packet's time comes from its sample count (from its first bytes). On a
 * page with a granule position, the packets that end there must add up to that
 * granule, so the last one absorbs any difference. A packet that spans pages
 * shares its time among its pieces by byte count.
 */
function timeMainStream(b: Uint8Array, main: Stream, frames: Segment[]): void {
  const t = main.timeOf!;
  let g = main.base ?? 0;
  const durState = { prev: 0 };
  type Pkt = { segs: Segment[]; bytes: number; d: number | null };
  let open: Pkt | null = null;
  const spread = (pk: Pkt, t0: number, t1: number) => {
    let acc = t0, done = 0;
    for (const seg of pk.segs) {
      done += seg.end - seg.start;
      const next = done === pk.bytes ? t1 : t0 + ((t1 - t0) * done) / (pk.bytes || 1);
      seg.time = [acc, Math.max(acc, next)];
      acc = seg.time[1];
    }
  };
  const pageSegs: [Page, Segment[]][] = [];
  for (const pg of main.pages) {
    if (pg.seg.kind !== 'audio') continue;
    const pieces = pagePieces(b, pg);
    const ended: Pkt[] = [];
    const audioSegs: Segment[] = [];
    const segs = pieceSegments(b, pg, pieces, (seg, pc) => {
      frames.push(seg);
      audioSegs.push(seg);
      if (pc.no < 0) { spread({ segs: [seg], bytes: 1, d: 0 }, t(g), t(g)); return; }
      if (pc.startsHere || !open) {
        if (open) spread(open, t(g), t(g)); // never finished
        open = { segs: [], bytes: 0, d: null };
      }
      open.segs.push(seg);
      open.bytes += seg.end - seg.start;
      if (pc.complete) {
        open.d = pc.startsHere ? packetDuration(main, b, pc.s, Math.min(pc.e, pc.s + 16), durState)
          : pg.contHead ? packetDuration(main, pg.contHead, 0, pg.contHead.length, durState) : null;
        if (open.d !== null && main.codec !== 'Opus') seg.summary += ` · ${open.d} samples out`;
        ended.push(open);
        open = null;
      }
    });
    pg.seg.children = [pageHeaderSegment(b, pg, pieces), ...segs];
    pageSegs.push([pg, audioSegs]);
    if (!ended.length) continue;
    const G = pg.granule === -1 ? NaN : Math.max(g, pg.granule);
    const known = ended.every((pk) => pk.d !== null);
    if (known || Number.isNaN(G)) {
      ended.forEach((pk, i) => {
        let g1 = g + (pk.d ?? 0);
        if (!Number.isNaN(G)) g1 = i === ended.length - 1 ? G : Math.min(g1, G);
        spread(pk, t(g), t(g1));
        g = g1;
      });
    } else {
      // Some sample counts are unknown: share the page's time by bytes.
      const total = ended.reduce((a, pk) => a + pk.bytes, 0);
      const g0 = g;
      let acc = 0;
      for (const pk of ended) {
        const ga = g0 + ((G - g0) * acc) / (total || 1);
        acc += pk.bytes;
        const gb = g0 + ((G - g0) * acc) / (total || 1);
        spread(pk, t(ga), t(gb));
      }
      g = G;
    }
  }
  if (open) spread(open, t(g), t(g)); // cut off at the end of the file
  // A page's time spans the times of its audio pieces.
  let prevT = 0;
  for (const [pg, segs] of pageSegs) {
    const t0 = segs.length ? segs[0].time![0] : prevT;
    const t1 = segs.length ? segs[segs.length - 1].time![1] : prevT;
    pg.seg.time = [t0, t1];
    pg.seg.summary += ` · ${fmtSec(t0)}–${fmtSec(t1)}`;
    prevT = t1;
  }
}

function parseOgg(b: Uint8Array): ParsedFile {
  const size = b.length;
  const segments: Segment[] = [];
  const notes: Note[] = [];
  const streams = new Map<number, Stream>();
  const streamList: Stream[] = [];
  const pages: Page[] = [];
  let garbage = 0, crcFails = 0, headerBytes = 0, truncatedPages = 0;

  const findOggS = (from: number) => {
    for (let i = from; i + 4 <= size; i++) if (b[i] === 0x4f && b[i + 1] === 0x67 && b[i + 2] === 0x67 && b[i + 3] === 0x53) return i;
    return -1;
  };
  const addGarbage = (start: number, end: number, why: string) => {
    segments.push({ kind: 'unknown', type: 'ogg.garbage', name: 'Unrecognized bytes', start, end, summary: fmtBytes(end - start), warning: why });
    garbage += end - start;
  };

  let p = 0;
  while (p < size) {
    if (!matches(b, p, 'OggS') || b[p + 4] !== 0 || p + 27 > size || p + 27 + b[p + 26] > size) {
      const cut = matches(b, p, 'OggS') && b[p + 4] === 0;
      const next = findOggS(p + 1);
      const end = next < 0 ? size : next;
      addGarbage(p, end, cut ? 'A page header that is cut off at the end of the file.' : 'These bytes are not an Ogg page. The parser skipped ahead to the next "OggS".');
      p = end;
      continue;
    }
    const nseg = b[p + 26];
    const bodyStart = p + 27 + nseg;
    let bodyLen = 0;
    for (let i = 0; i < nseg; i++) bodyLen += b[p + 27 + i];
    const declaredEnd = bodyStart + bodyLen;
    const end = Math.min(size, declaredEnd);
    const truncated = declaredEnd > size;
    const flags = b[p + 5];
    const granule = granuleAt(b, p + 6);
    const serial = u32le(b, p + 14), seq = u32le(b, p + 18), crcStored = u32le(b, p + 22);
    const crcCalc = truncated ? null : oggPageCrc(b, p, end);
    const warnings: string[] = [];
    if (truncated) { warnings.push(`The page is cut off: it needs ${fmtBytes(declaredEnd - size)} more.`); truncatedPages++; }
    if (crcCalc !== null && crcCalc !== crcStored) { warnings.push('The CRC does not match. These bytes are damaged.'); crcFails++; }
    headerBytes += bodyStart - p;

    let st = streams.get(serial);
    if (!st || (flags & 2 && st.pages.length > 0)) {
      st = new Stream(serial, streamList.length);
      streams.set(serial, st);
      streamList.push(st);
    }
    if (st.lastSeq >= 0 && seq !== st.lastSeq + 1) {
      warnings.push(`The page sequence jumps from ${st.lastSeq} to ${seq}. ${seq > st.lastSeq ? plural(seq - st.lastSeq - 1, 'page') + ' may be missing.' : ''}`.trim());
      // A packet left open before the gap cannot be finished by this page.
      if (st.open) {
        if (st.openHeader) st.headers.set(st.openNo, { detail: null, pieces: st.openPieces });
        st.open = false;
      }
    }
    st.lastSeq = seq;

    const pg: Page = {
      index: pages.length, start: p, end, bodyStart, nseg, flags, granule, seq, crcStored, crcCalc, truncated, stream: st,
      firstNewPacket: st.nextPacket, contNo: -1, contHead: null, orphan: false,
      completedAudio: 0, seg: null as unknown as Segment,
    };
    const s = st;
    if (flags & 1) {
      if (s.open) { pg.contNo = s.openNo; pg.contHead = s.headBuf.subarray(s.headOff, s.headEnd); } else pg.orphan = true;
    } else if (s.open) {
      warnings.push(`Packet ${s.openNo} never finished: the last page said it goes on, but this page starts a new packet.`);
      if (s.openHeader) s.headers.set(s.openNo, { detail: null, pieces: s.openPieces });
      s.open = false;
    }
    pg.firstNewPacket = s.nextPacket;

    let hasAudio = false, hasComment = false, hasHeader = false, first = true, sumDur = 0, durOk = true;
    const durState = { prev: s.prevBlock };
    walkLacing(b, p, nseg, bodyStart, end, (ps, pe, complete) => {
      const isFirst = first;
      first = false;
      if (isFirst && flags & 1 && pg.orphan) { hasAudio = AUDIO_CODECS.has(s.codec); return; }
      if (!s.open) {
        // A new packet starts here.
        const no = s.nextPacket++;
        if (no === 0) {
          s.codec = identify(b.subarray(ps, Math.min(pe, ps + 16)));
          if (s.codec === 'Vorbis') s.headerCount = 3;
          else if (s.codec === 'Opus') s.headerCount = 2;
          else if (s.codec === 'Speex') s.headerCount = 2; // updated by the header
          else if (s.codec === 'Theora') s.headerCount = 3;
        }
        // FLAC with an unknown header count: the first frame (sync 0xFFF8) ends the headers.
        if (s.codec === 'FLAC' && s.headerCount === Infinity && no > 0 && b[ps] === 0xff) s.headerCount = no;
        s.open = true;
        s.openNo = no;
        s.openLen = 0;
        s.headBuf = b;
        s.headOff = ps;
        s.headEnd = Math.min(pe, ps + 16);
        s.openHeader = no < s.headerCount;
        if (s.openHeader) s.openPieces = [];
      } else if (s.headEnd - s.headOff < 16) {
        const have = s.headBuf.subarray(s.headOff, s.headEnd);
        s.headBuf = concat(have, b.subarray(ps, Math.min(pe, ps + 16 - have.length)));
        s.headOff = 0;
        s.headEnd = s.headBuf.length;
      }
      const len = pe - ps;
      if (s.openHeader) {
        s.openPieces.push({ fileStart: ps, fileEnd: pe, logical: s.openLen });
        hasHeader = true;
        // The first byte tells us if a header packet holds tags.
        const h0 = s.headBuf[s.headOff];
        if ((s.codec === 'Vorbis' && h0 === 3) || (s.codec === 'Opus' && s.openNo === 1) || (s.codec === 'Speex' && s.openNo === 1) || (s.codec === 'FLAC' && s.openNo > 0 && ((h0 & 0x7f) === 4 || (h0 & 0x7f) === 6)))
          hasComment = true;
      } else hasAudio = true;
      s.openLen += len;
      if (!complete) return;
      // The packet ends on this page.
      s.open = false;
      if (s.openHeader) {
        const pieces = s.openPieces;
        let buf: Uint8Array;
        if (pieces.length === 1) buf = b.subarray(pieces[0].fileStart, pieces[0].fileEnd);
        else {
          buf = new Uint8Array(s.openLen);
          for (const pc of pieces) buf.set(b.subarray(pc.fileStart, pc.fileEnd), pc.logical);
        }
        s.headerBytes += s.openLen;
        const detail = parseHeader(s, s.openNo, buf);
        s.headers.set(s.openNo, { detail, pieces });
        if (detail?.picture) {
          // Only point at the picture if its bytes are contiguous in the file.
          const pic = detail.picture;
          const pc = pieces.find((x) => pic.start >= x.logical && pic.end <= x.logical + (x.fileEnd - x.fileStart));
          if (pc) s.pictures.push({ ...pic, start: pic.start - pc.logical + pc.fileStart, end: pic.end - pc.logical + pc.fileStart });
          else s.notes.push({ level: 'info', text: 'A picture block is split across pages, so its bytes are not in one place and cannot be shown.' });
        }
      } else {
        const d = packetDuration(s, s.headBuf, s.headOff, s.headEnd, durState);
        if (d === null) durOk = false;
        else sumDur += d;
        s.audioBytes += s.openLen;
        s.audioPackets++;
        pg.completedAudio++;
      }
    });
    s.prevBlock = durState.prev;

    if (pg.completedAudio > 0) {
      if (!durOk) s.durKnown = false;
      if (granule === -1) warnings.push('Packets end on this page, but its granule position is -1. That breaks the Ogg rules.');
      else {
        // The first audio page tells us where the stream starts: its granule minus
        // the samples its packets produce. It is 0 unless the stream was cut
        // from a longer one. A negative result means "trim the start".
        if (s.base === null) s.base = durOk ? Math.max(0, granule - sumDur) : 0;
        const from = Number.isNaN(s.lastAudioGranule) ? s.base : s.lastAudioGranule;
        if (granule < from) warnings.push('The granule position goes backwards here.');
        s.lastExpectedEnd = durOk ? from + sumDur : NaN;
        s.lastAudioGranule = granule;
      }
    }
    if (flags & 4) s.eos = true;

    const kind: SegmentKind = s.codec === 'Unknown' && !hasHeader ? 'unknown'
      : hasAudio ? (AUDIO_CODECS.has(s.codec) ? 'audio' : 'unknown')
      : hasComment ? 'metadata' : 'header';
    let summary: string;
    if (hasAudio) summary = `${s.codec} audio · ${plural(pg.completedAudio, 'packet')} end here · ${fmtBytes(end - p)}`;
    else if (hasHeader) summary = `${s.codec} headers · ${fmtBytes(end - p)}`;
    else summary = fmtBytes(end - p);
    const seg: Segment = {
      kind, type: 'ogg.page', name: `Page ${pg.index}`, start: p, end, summary,
      warning: warnings.length ? warnings.join(' ') : undefined,
      children: () => pageChildren(b, pg),
    };
    pg.seg = seg;
    pages.push(pg);
    s.pages.push(pg);
    segments.push(seg);
    p = end;
  }

  // ---- Timing for the first audio stream ---------------------------------
  const main = streamList.find((s) => AUDIO_CODECS.has(s.codec));
  const frames: Segment[] = [];
  let duration: number | undefined;
  let totalSamples: number | undefined;
  let padding: number | undefined;
  if (main && main.base !== null) {
    const rate = main.codec === 'Opus' ? 48000 : main.rate;
    const skip = main.codec === 'Opus' ? main.preSkip : 0;
    const base = main.base;
    if (rate > 0) main.timeOf = (g: number) => Math.max(0, g - base - skip) / rate;
  }
  if (main?.timeOf) {
    const t = main.timeOf;
    timeMainStream(b, main, frames);
    if (!Number.isNaN(main.lastAudioGranule)) {
      duration = t(main.lastAudioGranule);
      totalSamples = Math.max(0, main.lastAudioGranule - (main.base ?? 0) - (main.codec === 'Opus' ? main.preSkip : 0));
      if (main.eos && main.lastExpectedEnd > main.lastAudioGranule) padding = main.lastExpectedEnd - main.lastAudioGranule;
    }
  }

  // ---- Audio info ---------------------------------------------------------
  const audio: AudioInfo = { codec: main?.codec ?? (streamList[0]?.codec ?? 'Unknown'), lossless: main?.codec === 'FLAC' };
  if (main) {
    audio.codecLong = main.codecLong || undefined;
    audio.sampleRate = main.codec === 'Opus' ? 48000 : main.rate || undefined;
    audio.channels = main.channels || undefined;
    if (audio.channels) audio.channelMode = channelName(audio.channels);
    if (main.codec === 'FLAC') { audio.bitsPerSample = main.bitsPerSample || undefined; audio.bitrateMode = 'Lossless'; }
    else if (main.codec === 'Vorbis' || main.codec === 'Opus') audio.bitrateMode = 'VBR';
    audio.duration = duration;
    audio.totalSamples = totalSamples ?? (main.totalSamples || undefined);
    if (duration && duration > 0) audio.bitrate = Math.round((main.audioBytes * 8) / duration);
    audio.encoder = main.vendor || undefined;
    if (main.codec === 'Opus') audio.encoderDelay = main.preSkip;
    if (padding) audio.encoderPadding = padding;
  }

  // ---- Notes ----------------------------------------------------------------
  if (pages.length) {
    notes.push({
      level: 'info',
      text: `The file has ${plural(pages.length, 'page')}. Page headers and lacing tables take ${fmtBytes(headerBytes)}, which is ${((headerBytes / size) * 100).toFixed(2)}% of the file.`,
    });
  }
  if (main?.codec === 'Opus') {
    notes.push({ level: 'info', text: `Pre-skip is ${main.preSkip} samples (${ms(main.preSkip)}). The player drops them, because the encoder needs a short warm-up before its output is clean.` });
    if (main.inputRate && main.inputRate !== 48000)
      notes.push({ level: 'info', text: `The source audio was ${fmtHz(main.inputRate)}. Opus always decodes at 48 kHz, so the input rate is only a hint for playback.` });
  }
  if (main?.codec === 'Vorbis' && main.nominal > 0 && audio.bitrate)
    notes.push({ level: 'info', text: `The header says the nominal bitrate is ${fmtKbps(main.nominal)}; the real average is ${fmtKbps(audio.bitrate)}. Vorbis is variable-bitrate, so the nominal value is only a target.` });
  if (main?.codec === 'Vorbis' && !main.modeFlags)
    notes.push({ level: 'warn', text: 'We could not read the mode list from the setup header, so packet block sizes and times are estimates.' });
  if (padding)
    notes.push({ level: 'info', text: `The last page's granule position is ${plural(padding, 'sample')} short of what its packets hold. This tells the player to cut the encoder's padding at the end.` });
  if (main && main.base && main.base > 0)
    notes.push({ level: 'info', text: `The stream starts at granule ${main.base.toLocaleString('en-US')}, not 0. It was probably cut from a longer stream. Times here start at 0.` });
  if (crcFails) notes.push({ level: 'warn', text: `${plural(crcFails, 'page')} failed the CRC check. Their bytes are damaged, and a player may skip them.` });
  else if (pages.length && pages.every((q) => q.crcCalc !== null || q.truncated)) notes.push({ level: 'info', text: 'Every complete page passed its CRC-32 check.' });
  if (truncatedPages) notes.push({ level: 'warn', text: 'The last page is cut off. The file probably did not finish downloading or copying.' });
  if (garbage) notes.push({ level: 'warn', text: `${fmtBytes(garbage)} of the file is not part of any page. The parser found the next "OggS" and went on.` });
  const minus1 = main ? main.pages.filter((q) => q.granule === -1).length : 0;
  if (minus1) notes.push({ level: 'info', text: `${plural(minus1, 'page')} have granule −1: no packet ends on them, so they get their time from the next page.` });
  for (const s of streamList) {
    if (!AUDIO_CODECS.has(s.codec) && s.codec !== 'Unknown' && s !== main) continue;
    if (s.pages.length && !s.eos && !truncatedPages)
      notes.push({ level: 'info', text: `Stream ${hex(s.serial, 8)} has no end-of-stream (EOS) page. The file may be cut short, or the writer did not close the stream.` });
  }
  if (main?.eos) notes.push({ level: 'info', text: 'The last page of the stream has the end-of-stream (EOS) flag, so the file is complete.' });
  if (streamList.length > 1) {
    const desc = streamList.map((s) => `${s.codec} (${hex(s.serial, 8)}, ${plural(s.pages.length, 'page')})`).join('; ');
    const chained = streamList.length > 1 && streamList.every((s, i) => i === 0 || s.pages[0].index > streamList[i - 1].pages[streamList[i - 1].pages.length - 1].index);
    notes.push({
      level: 'info',
      text: `This file holds ${streamList.length} logical streams${chained ? ' one after another (a chained file)' : ' mixed together (multiplexed)'}: ${desc}. Only the first audio stream is linked to time.`,
    });
  }
  if (main) notes.push(...main.notes);

  return {
    format: 'ogg', container: 'Ogg', size, segments, frames, audio,
    tags: main?.tags ?? [], pictures: main?.pictures ?? [], notes,
  };
}

// ---------------------------------------------------------------------------

export const oggExplanations: Record<string, Explanation> = {
  ...vorbisCommentExplanations,
  'ogg.page': {
    title: 'Ogg page',
    body: 'Ogg cuts every stream into **pages** of up to about 64 KB. Each page has its own header with a checksum and a timestamp. If a page is lost or damaged, a player can find the next "OggS" and go on playing.',
  },
  'ogg.pageHeader': {
    title: 'Page header',
    body: 'A 27-byte header, then a **lacing table**. The table lists packet sizes in 255-byte steps: a value of 255 means "more follows", and a smaller value ends a packet. This lets small packets pack tightly and big packets span pages. The **granule position** counts samples, so a player can seek by time without decoding.',
  },
  'ogg.garbage': {
    title: 'Unrecognized bytes',
    body: 'These bytes are not part of any Ogg page. They can be damage or junk from an editor. Because every page starts with "OggS", a reader can skip to the next page and go on.',
  },
  'ogg.packet': {
    title: 'Packet',
    body: 'A packet is one unit of codec data. Ogg does not look inside packets; it only carries them. A packet can span pages, so one packet may show up in several pieces.',
  },
  'ogg.vorbis.id': {
    title: 'Vorbis identification header',
    body: 'The first packet of a Vorbis stream. It gives the channel count, sample rate, bitrate hints and the two block sizes. Vorbis switches between short and long blocks: short ones for sharp attacks, long ones for steady tones.',
  },
  'ogg.vorbis.comment': {
    title: 'Vorbis comment header',
    body: 'The second Vorbis packet. It holds the tags (title, artist and so on) as plain `NAME=value` text, plus a vendor string that names the encoder library.',
  },
  'ogg.vorbis.framing': {
    title: 'Framing bit',
    body: 'A single 1 bit at the end of a Vorbis header. It is a simple check that the reader got to the right place.',
  },
  'ogg.vorbis.setup': {
    title: 'Vorbis setup header',
    body: 'The third Vorbis packet. It holds the codebooks and all the decoder settings, so it is often a few KB. Vorbis puts these in the file, not in the decoder, so encoders can improve without breaking old players. The **modes** at the end tell each audio packet which block size it uses.',
  },
  'ogg.vorbis.audio': {
    title: 'Vorbis audio packet',
    body: 'One block of compressed audio. Its first bit says "audio", then a **mode** number picks a short or long block. Blocks overlap by half, so each packet adds a quarter of the previous block plus a quarter of this one in new samples.',
  },
  'ogg.opus.head': {
    title: 'OpusHead',
    body: 'The first packet of an Opus stream. It gives the channel count, the **pre-skip** (samples to drop at the start) and an output gain. Opus always decodes at 48 kHz; the input sample rate here is only a note about the source.',
  },
  'ogg.opus.tags': {
    title: 'OpusTags',
    body: 'The second Opus packet. It holds the tags as Vorbis comments: a vendor string, then `NAME=value` pairs.',
  },
  'ogg.opus.tagsExtra': {
    title: 'Extra data after tags',
    body: 'Bytes after the last comment. Encoders can leave padding here so tags can grow later without rewriting the whole file.',
  },
  'ogg.opus.audio': {
    title: 'Opus audio packet',
    body: 'One Opus packet, 2.5 to 120 ms of sound. The first byte (the **TOC**) says which coder was used: **SILK** models the human voice and is very efficient for speech; **CELT** is a transform coder that suits music; **Hybrid** uses SILK for low tones and CELT for high ones. Opus can switch mode on every packet.',
  },
  'ogg.flac.head': {
    title: 'Ogg FLAC header',
    body: 'The first packet of FLAC in Ogg. A short Ogg mapping header comes first, then the normal FLAC STREAMINFO block: sample rate, channels, bit depth, total samples and an MD5 of the audio.',
  },
  'ogg.flac.metadata': {
    title: 'FLAC metadata block',
    body: 'A normal FLAC metadata block, carried as its own Ogg packet. The first byte says if it is the last block and what type it is.',
  },
  'ogg.flac.audio': {
    title: 'FLAC frame',
    body: 'One FLAC audio frame. It starts with a sync code and a small header, then holds the samples, compressed without any loss. In Ogg, each frame is one packet.',
  },
  'ogg.speex.head': {
    title: 'Speex header',
    body: 'The first packet of a Speex stream. Speex is an older codec made only for speech. The header gives the sample rate, mode and frame size.',
  },
  'ogg.speex.comment': {
    title: 'Speex comments',
    body: 'The second Speex packet. It holds the tags as Vorbis comments.',
  },
  'ogg.speex.audio': {
    title: 'Speex audio packet',
    body: 'One packet of Speex speech data. It holds one or more fixed-size frames, usually 20 ms each.',
  },
};

export const oggParser: FormatParser = {
  id: 'ogg',
  detect: (b) => matches(b, 0, 'OggS') && b.length > 4 && b[4] === 0,
  parse: parseOgg,
  explanations: oggExplanations,
};
